import type { IntlShape } from 'react-intl'

interface MultiCityFlightMessageArgs {
  intl: IntlShape
  from: string
  to: string
  selectedJourneyList: Array<App.JourneyV2>
  groupCurrency: string | undefined
  combinedTotal: number
  /** Renders the per-leg context block; each entry point keeps its own. */
  buildFlightContext: (journey: App.JourneyV2) => string
}

/**
 * Builds the user-facing chat message and the assistant context for adding a
 * multi-city itinerary. Shared by the streamed-card search and the flight
 * search drawer so the grouping fields, combined price/currency, journey ids and
 * per-leg context never drift between the two entry points. Pure given its args
 * (price/currency are computed by the caller, since their data sources differ).
 */
export function buildMultiCityFlightMessage({
  intl,
  from,
  to,
  selectedJourneyList,
  groupCurrency,
  combinedTotal,
  buildFlightContext,
}: MultiCityFlightMessageArgs): { userMessage: string; contextContent: string } {
  const userMessage = intl.formatMessage(
    {
      id: 'icHop6Aw2KYzb_NvSmi9',
      defaultMessage: 'Add multi-city flight from {from} to {to}',
      description: 'User chat message shown when adding a multi-city flight itinerary from the chat flight search results',
    },
    { from, to },
  )

  const legsContext = selectedJourneyList
    .map((journey, index) => `Flight ${index + 1}:\n${buildFlightContext(journey)}`)
    .join('\n\n')

  // Tell the model to persist the legs as one group with the combined total.
  // The combined total is provided explicitly so it is never re-derived by
  // summing per-leg fares downstream.
  const contextContent = [
    `This is a single multi-city itinerary with ${selectedJourneyList.length} legs. Save each leg as a separate flight that shares one flightGroupId, sets flightGroupType=multi_city and an ordered legOrder starting at 0, preserves the Journey ID as journeyId, and sets flightGroupTotalPrice=${combinedTotal} with flightGroupTotalCurrencyCode=${groupCurrency} on every leg.`,
    `Combined itinerary total: ${groupCurrency} ${combinedTotal}`,
    legsContext,
  ].join('\n\n')

  return { userMessage, contextContent }
}
